import api from '../libs/axiosInstance';
import firebase from 'firebase/app';
import 'firebase/auth';

interface TrialStatus {
  userId: string;
  trialStartDate: string;
  trialEndDate: string;
  trialDurationDays: number;
  planName: string;
  trialStatus: 'active' | 'ending_soon' | 'ended' | 'converted' | 'cancelled';
  daysRemaining: number;
  isActive: boolean;
  isEndingSoon: boolean;
  hasEnded: boolean;
  hasConverted: boolean;
  trialUsage: {
    sessionsCreated: number;
    jobAnalysesUsed: number;
    cvsGenerated: number;
    featuresExplored: string[];
    lastActivityDate: string;
  };
}

interface CreateTrialRequest {
  planName: string;
  trialDurationDays?: number;
  timezone?: string;
}

interface TrackUsageRequest {
  action: 'session_created' | 'job_analysis_used' | 'cv_generated' | 'feature_explored';
  metadata?: {
    feature?: string;
    sessionType?: string;
  };
}

class TrialServiceClass {

  /**
   * Get current trial status for user
   */
  async getCurrentTrialStatus(): Promise<TrialStatus | null> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/trial-management/${user.uid}/current`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error getting trial status:', error);
      return null;
    }
  }

  /**
   * Create trial period for current user
   */
  async createTrial(request: CreateTrialRequest): Promise<TrialStatus> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`/trial-management/${user.uid}/create`, request);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to create trial');
    } catch (error) {
      console.error('Error creating trial:', error);
      throw error;
    }
  }

  /**
   * Track trial usage
   */
  async trackUsage(request: TrackUsageRequest): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        return; // Don't throw for usage tracking
      }

      await api.post(`/trial-management/${user.uid}/track-usage`, request);
    } catch (error) {
      console.error('Error tracking trial usage:', error);
      // Don't throw - usage tracking shouldn't break user flow
    }
  }

  /**
   * Convert trial to paid subscription
   */
  async convertTrial(subscriptionId: string, convertedToPlan: string): Promise<any> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`/trial-management/${user.uid}/convert`, {
        subscriptionId,
        convertedToPlan,
        conversionSource: 'manual'
      });

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to convert trial');
    } catch (error) {
      console.error('Error converting trial:', error);
      throw error;
    }
  }

  /**
   * Check if user is in trial
   */
  async isUserInTrial(): Promise<boolean> {
    try {
      const status = await this.getCurrentTrialStatus();
      return status ? (status.isActive || status.isEndingSoon) : false;
    } catch (error) {
      console.error('Error checking trial status:', error);
      return false;
    }
  }

  /**
   * Get trial progress percentage
   */
  async getTrialProgress(): Promise<{
    percentage: number;
    daysUsed: number;
    daysRemaining: number;
    totalDays: number;
  }> {
    try {
      const status = await this.getCurrentTrialStatus();

      if (!status) {
        return { percentage: 0, daysUsed: 0, daysRemaining: 0, totalDays: 0 };
      }

      const daysUsed = status.trialDurationDays - status.daysRemaining;
      const percentage = (daysUsed / status.trialDurationDays) * 100;

      return {
        percentage: Math.max(0, Math.min(100, percentage)),
        daysUsed,
        daysRemaining: status.daysRemaining,
        totalDays: status.trialDurationDays
      };
    } catch (error) {
      console.error('Error getting trial progress:', error);
      return { percentage: 0, daysUsed: 0, daysRemaining: 0, totalDays: 0 };
    }
  }

  /**
   * Get trial usage summary
   */
  async getTrialUsageSummary(): Promise<{
    totalActions: number;
    mostUsedFeature: string;
    usageScore: number;
    lastActivity: string;
  }> {
    try {
      const status = await this.getCurrentTrialStatus();

      if (!status || !status.trialUsage) {
        return {
          totalActions: 0,
          mostUsedFeature: 'none',
          usageScore: 0,
          lastActivity: 'never'
        };
      }

      const usage = status.trialUsage;
      const totalActions = usage.sessionsCreated + usage.jobAnalysesUsed + usage.cvsGenerated;

      // Determine most used feature
      let mostUsedFeature = 'none';
      if (usage.sessionsCreated > usage.jobAnalysesUsed && usage.sessionsCreated > usage.cvsGenerated) {
        mostUsedFeature = 'Interview Practice';
      } else if (usage.jobAnalysesUsed > usage.cvsGenerated) {
        mostUsedFeature = 'Job Analysis';
      } else if (usage.cvsGenerated > 0) {
        mostUsedFeature = 'CV Generation';
      }

      // Calculate usage score (0-100)
      let usageScore = 0;
      usageScore += Math.min(usage.sessionsCreated * 10, 30);
      usageScore += Math.min(usage.jobAnalysesUsed * 15, 30);
      usageScore += Math.min(usage.cvsGenerated * 20, 25);
      usageScore += Math.min(usage.featuresExplored.length * 3, 15);

      return {
        totalActions,
        mostUsedFeature,
        usageScore: Math.min(usageScore, 100),
        lastActivity: usage.lastActivityDate
      };
    } catch (error) {
      console.error('Error getting trial usage summary:', error);
      return {
        totalActions: 0,
        mostUsedFeature: 'none',
        usageScore: 0,
        lastActivity: 'never'
      };
    }
  }

  /**
   * Format trial status for display
   */
  formatTrialStatus(status: TrialStatus['trialStatus']): string {
    const statusMap = {
      'active': 'Active Trial',
      'ending_soon': 'Trial Ending Soon',
      'ended': 'Trial Ended',
      'converted': 'Converted to Paid',
      'cancelled': 'Trial Cancelled'
    };
    return statusMap[status] || 'Unknown Status';
  }

  /**
   * Get urgency level for UI styling
   */
  getTrialUrgency(daysRemaining: number): 'low' | 'medium' | 'high' | 'critical' {
    if (daysRemaining <= 0) return 'critical';
    if (daysRemaining <= 1) return 'high';
    if (daysRemaining <= 3) return 'medium';
    return 'low';
  }

  /**
   * Get recommended actions based on trial status
   */
  getRecommendedActions(status: TrialStatus): Array<{
    action: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    link?: string;
  }> {
    const actions = [];

    if (status.isActive) {
      if (status.trialUsage.sessionsCreated === 0) {
        actions.push({
          action: 'Start Your First Session',
          description: 'Experience AI-powered interview practice',
          priority: 'high' as const,
          link: '/new-session'
        });
      }

      if (status.trialUsage.jobAnalysesUsed === 0) {
        actions.push({
          action: 'Analyze a Job Posting',
          description: 'See how AI can help you understand job requirements',
          priority: 'high' as const,
          link: '/job-analysis'
        });
      }

      if (status.daysRemaining <= 3) {
        actions.push({
          action: 'Upgrade Your Plan',
          description: 'Continue your career growth without interruption',
          priority: 'high' as const,
          link: '/billing'
        });
      }
    }

    if (status.hasEnded) {
      actions.push({
        action: 'Restore Premium Access',
        description: 'Get back all the features you were using',
        priority: 'high' as const,
        link: '/billing'
      });
    }

    return actions;
  }
}

// Export singleton instance
export const trialService = new TrialServiceClass();
